/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import AIAssistMessage from "./AIAssistMessage";
import { AIMessage, AssistantMessage, UserMessage } from "@interfaces/AIAssistTypes";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PF_SERVER_URL } from "../../Constants";
import { userAtom } from "@state/State";
import { useRecoilValueLoadable } from "recoil";
import { EvidenceCustomTag, IEvidence, IUser } from "@interfaces/Interfaces";
import { evidencePreviewFields } from "@utils/aiAssistUtils";

const generateMessageId = (role: "user" | "assistant"): string => {
  const now = new Date();
  const timestamp = now.getTime();
  return `${role}-${timestamp}-${Math.random().toString(36).substring(2, 15)}`;
};

const generateInitialMessages = () => {
  const initialMessages: AIMessage[] = [
    {
      id: generateMessageId("assistant"),
      role: "assistant",
      type: "prompt",
      content: "Use your microphone or start typing to describe your CPD",
    },
    {
      id: generateMessageId("user"),
      role: "user",
      status: "message-input",
    },
  ];

  return initialMessages;
};

const generateSessionId = () => {
  return `session-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
};

const AI_EVIDENCE_PREVIEW_ENDPOINT = `${PF_SERVER_URL}/portfolio/AI/evidence-string2json`;
const AI_EVIDENCE_PREVIEW_TIMEOUT = 180_000; // 3 minutes

type Props = {
  onEvidencePreviewSaved: (params: { evidence: IEvidence | null; sessionId: string; messages: AIMessage[] }) => void;
  initialEvidencePreview?: IEvidence | null;
};

const AIAssistFlow: React.FC<Props> = (props) => {
  const queryClient = useQueryClient();
  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const sessionId = useMemo(() => generateSessionId(), []);

  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [messageToFetch, setMessageToFetch] = useState<string | null>(null);
  const [evidenceToPass, setEvidenceToPass] = useState<IEvidence | null>(null);
  const [loadingMessageId, setLoadingMessageId] = useState<string | null>(null);

  const getMissingMandatoryFields = (evidence: IEvidence): string[] | null => {
    const mandatoryFields = evidencePreviewFields.filter((field) => field.required);
    const missingMandatoryFields = mandatoryFields.filter((field) => !evidence[field.key as keyof IEvidence]);

    return missingMandatoryFields.length > 0 ? missingMandatoryFields.map((field) => field.label) : null;
  };

  const getMissingMandatoryFieldsMessage = (missingMandatoryFields: string[] | null) => {
    if (!missingMandatoryFields || missingMandatoryFields.length === 0) return "";

    const fieldsStr =
      missingMandatoryFields.length === 1
        ? `<b>${missingMandatoryFields[0]}</b>`
        : missingMandatoryFields.length === 2
          ? `<b>${missingMandatoryFields[0]}</b> and <b>${missingMandatoryFields[1]}</b>`
          : missingMandatoryFields
              .map((field, idx, arr) => {
                if (idx === arr.length - 1) return `and <b>${field}</b>`;
                return `<b>${field}</b>`;
              })
              .join(", ")
              .replace(", and", " and");

    return ` We're missing your ${fieldsStr}.`;
  };

  useEffect(() => {
    if (props.initialEvidencePreview) {
      // If there's an initial evidence preview, start with that as the first message
      const missingMandatoryFields = getMissingMandatoryFields(props.initialEvidencePreview);
      const missingMandatoryFieldsMessage = getMissingMandatoryFieldsMessage(missingMandatoryFields);

      const evidencePreviewMessage: AssistantMessage = {
        id: generateMessageId("assistant"),
        role: "assistant",
        type: "evidence-preview",
        data: props.initialEvidencePreview,
      };

      const evidencePreviewSaveMessage: AssistantMessage = {
        id: generateMessageId("assistant"),
        role: "assistant",
        type: "evidence-preview-save",
        content: `Here's your evidence preview.${missingMandatoryFieldsMessage} How would you like to continue?`,
      };

      const firstTimeout = setTimeout(() => {
        setMessages((prevMessages) => [...prevMessages, evidencePreviewMessage]);
      }, 500);

      const secondTimeout = setTimeout(() => {
        setMessages((prevMessages) => [...prevMessages, evidencePreviewSaveMessage]);
      }, 1000);

      setEvidenceToPass(props.initialEvidencePreview);

      return () => {
        clearTimeout(firstTimeout);
        clearTimeout(secondTimeout);
      };
    } else {
      // Default behavior: show prompt and input
      const initialMessages = generateInitialMessages();

      const firstTimeout = setTimeout(() => {
        setMessages((prevMessages) => [...prevMessages, initialMessages[0]]);
      }, 500);

      const secondTimeout = setTimeout(() => {
        setMessages((prevMessages) => [...prevMessages, initialMessages[1]]);
      }, 1000);

      return () => {
        clearTimeout(firstTimeout);
        clearTimeout(secondTimeout);
      };
    }
  }, [props.initialEvidencePreview]);

  const generateEvidencePreview = useCallback(
    async (messageInput: string) => {
      let input: string;

      if (evidenceToPass) {
        input = messageInput + " " + JSON.stringify(evidenceToPass);
      } else {
        input = messageInput;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), AI_EVIDENCE_PREVIEW_TIMEOUT);

      try {
        const response: Response = await fetch(AI_EVIDENCE_PREVIEW_ENDPOINT, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            string: input,
            token: user.contents?.token,
            contactID: user.contents?.userData.contactID,
          }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response) {
          const json = await response.json();
          if (json) {
            return json.body;
          }
        } else {
          return null;
        }
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === "AbortError") {
          throw new Error("Request timed out after 20 seconds");
        }
        throw error;
      }

      return null;
    },
    [user, evidenceToPass]
  );

  const {
    data: queryResponse,
    error: queryError,
    isError,
  } = useQuery({
    queryKey: ["generate-evidence-preview", messageToFetch, evidenceToPass],
    queryFn: () => {
      if (!messageToFetch) return null;
      return generateEvidencePreview(messageToFetch);
    },
    enabled: !!messageToFetch,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  const processEvidence = (evidence: Record<string, any>) => {
    const newObject: IEvidence = {
      ...(evidence.activity && {
        activity: evidence.activity,
      }),
      ...(evidence.activityDate &&
        evidence.activityDate !== "." && {
          activityDate: evidence.activityDate,
        }),
      ...(evidence.activityDescription &&
        evidence.activityDescription !== "." && {
          activityDescription: evidence.activityDescription,
        }),
      ...(evidence.numberOfHours &&
        evidence.numberOfHours >= 0 && {
          numberOfHours: evidence.numberOfHours,
        }),
      ...(evidence.date && evidence.date !== "." && { date: evidence.date }),
      ...(evidence.benefits && evidence.benefits !== "." && { benefits: evidence.benefits }),
      ...(evidence.objectives && evidence.objectives !== "." && { objectives: evidence.objectives }),
      ...(evidence.reflections && evidence.reflections !== "." && { reflections: evidence.reflections }),
      ...(evidence.title && evidence.title !== "." && { title: evidence.title }),
      ...(evidence.customTags &&
        evidence.customTags.length > 0 && {
          customTags: evidence.customTags.filter((item: EvidenceCustomTag) => item.tag.length > 0),
        }),
    };

    return newObject;
  };

  const addAssistantMessageWithDelay = useCallback((message: AssistantMessage, delay: number | null = null) => {
    const delayToUse = delay ?? Math.random() * 1000 + 1000;

    setTimeout(() => {
      setMessages((prevMessages) => [...prevMessages, message]);
    }, delayToUse);
  }, []);

  useEffect(() => {
    if (queryResponse && messageToFetch) {
      if (loadingMessageId) {
        setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== loadingMessageId));
        setLoadingMessageId(null);
      }

      const processedEvidence = processEvidence(queryResponse);
      const newAssistantMsg: AssistantMessage = {
        id: generateMessageId("assistant"),
        role: "assistant",
        type: "evidence-preview",
        data: processedEvidence,
      };
      setMessages((prevMessages) => [...prevMessages, newAssistantMsg]);
      const missingMandatoryFields = getMissingMandatoryFields(processedEvidence);
      const missingMandatoryFieldsMessage = getMissingMandatoryFieldsMessage(missingMandatoryFields);

      const newAssistantMsg2: AssistantMessage = {
        id: generateMessageId("assistant"),
        role: "assistant",
        type: "evidence-preview-save",
        content: `Here's your evidence preview.${missingMandatoryFieldsMessage} How would you like to continue?`,
      };
      setMessages((prevMessages) => [...prevMessages, newAssistantMsg2]);

      setEvidenceToPass(processedEvidence);
      // Clear the query cache for this specific message to allow fresh fetching if needed
      queryClient.removeQueries({ queryKey: ["generate-evidence-preview", messageToFetch] });
      setMessageToFetch(null);
    }
  }, [queryResponse, messageToFetch, queryClient, addAssistantMessageWithDelay, loadingMessageId]);

  useEffect(() => {
    if (isError && queryError && messageToFetch) {
      if (loadingMessageId) {
        setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== loadingMessageId));
        setLoadingMessageId(null);
      }

      const errorMessage: AssistantMessage = {
        id: generateMessageId("assistant"),
        role: "assistant",
        type: "evidence-preview-error",
        content:
          queryError instanceof Error && queryError.message.includes("timed out")
            ? "The request took too long to respond. Please try again."
            : "Something went wrong while generating your evidence preview. Please try again.",
      };
      setMessages((prevMessages) => [...prevMessages, errorMessage]);

      queryClient.removeQueries({ queryKey: ["generate-evidence-preview", messageToFetch] });
      setMessageToFetch(null);
    }
  }, [isError, queryError, messageToFetch, queryClient, loadingMessageId]);

  const generatePromptResponse = (message: UserMessage) => {
    if (message.status === "evidence-pending") {
      const newMessage: AssistantMessage = {
        id: generateMessageId("assistant"),
        role: "assistant",
        type: "re-prompt",
        content: "Sounds great! Do you want me to generate your evidence preview?",
      };

      addAssistantMessageWithDelay(newMessage);
    }
  };

  const handleUserMessage = (message: string) => {
    const lastMessage = messages[messages.length - 1];

    if ("status" in lastMessage && lastMessage.status === "message-input") {
      const newMessage: UserMessage = {
        ...lastMessage,
        status: "evidence-pending",
        content: message,
      };
      setMessages((prevMessages) => [...prevMessages.slice(0, -1), newMessage]);
      generatePromptResponse(newMessage);
    }
  };

  const handleUserRefineMessage = (data: IEvidence, message: string) => {
    const lastMessage = messages[messages.length - 1];

    if ("status" in lastMessage && lastMessage.status === "evidence-preview-refine") {
      const newMessage: UserMessage = {
        ...lastMessage,
        status: "evidence-pending",
        content: message,
        data,
      };
      setMessages((prevMessages) => [...prevMessages.slice(0, -1), newMessage]);
      addAssistantMessageWithDelay(
        {
          id: generateMessageId("assistant"),
          role: "assistant",
          type: "evidence-refine-prerequest",
          content: "Thanks, give me a minute to update your preview",
        },
        1000
      );

      const loadingMsg: AssistantMessage = {
        id: generateMessageId("assistant"),
        role: "assistant",
        type: "evidence-preview-loading",
      };
      setLoadingMessageId(loadingMsg.id);
      setTimeout(() => {
        setMessages((prevMessages) => [...prevMessages, loadingMsg]);
      }, 2000);

      setEvidenceToPass(data);
      setMessageToFetch(message);
    }
  };

  const handleRePrompt = () => {
    const newMessage: UserMessage = {
      id: generateMessageId("user"),
      role: "user",
      status: "message-input",
    };
    const lastMessage: AssistantMessage = messages[messages.length - 1] as AssistantMessage;

    if (lastMessage.type === "re-prompt") {
      setMessages((prevMessages) => [
        ...prevMessages.slice(0, -1),
        { ...lastMessage, type: "re-prompt-handled" },
        newMessage,
      ]);
    }
  };

  const handleGenerateEvidencePreview = () => {
    const lastMessage = messages[messages.length - 1];
    setMessages((prevMessages) => [...prevMessages.slice(0, -1), { ...lastMessage, type: "re-prompt-handled" }]);

    const userMessage = messages.find((msg): msg is UserMessage => {
      return (
        msg.role === "user" &&
        "status" in msg &&
        msg.status === "evidence-pending" &&
        typeof msg.content === "string" &&
        msg.content.length > 0
      );
    });

    if (!userMessage?.content) {
      console.error("No user message content found");
      return;
    }

    const loadingMsg: AssistantMessage = {
      id: generateMessageId("assistant"),
      role: "assistant",
      type: "evidence-preview-loading",
    };
    setLoadingMessageId(loadingMsg.id);
    setMessages((prevMessages) => [...prevMessages, loadingMsg]);

    // Set the message to fetch, which will trigger the useQuery hook
    setMessageToFetch(userMessage.content);
  };

  const handleRefineEvidencePreview = () => {
    const latestEvidencePreview = [...messages]
      .reverse()
      .find((msg) => msg.role === "assistant" && "type" in msg && msg.type === "evidence-preview" && msg.data);

    if (latestEvidencePreview) {
      const lastMessage = messages[messages.length - 1] as AssistantMessage;
      const newMessage: UserMessage = {
        id: generateMessageId("user"),
        role: "user",
        status: "evidence-preview-refine",
        data: latestEvidencePreview.data,
      };
      setMessages((prevMessages) => [
        ...prevMessages.slice(0, -1),
        { ...lastMessage, type: "evidence-preview-save-handled" },
        newMessage,
      ]);
    }
  };

  const handleSaveEvidencePreview = () => {
    props.onEvidencePreviewSaved?.({ evidence: evidenceToPass, sessionId, messages });
    setEvidenceToPass(null);
  };

  const handleRetryEvidencePreview = () => {
    const userMessage = messages.find((msg): msg is UserMessage => {
      return (
        msg.role === "user" &&
        "status" in msg &&
        msg.status === "evidence-pending" &&
        typeof msg.content === "string" &&
        msg.content.length > 0
      );
    });

    if (!userMessage?.content) {
      console.error("No user message content found for retry");
      return;
    }

    // Replace the error message with the handled version
    setMessages((prevMessages) =>
      prevMessages.map((msg) => {
        if ("type" in msg && (msg as AssistantMessage).type === "evidence-preview-error") {
          return {
            ...msg,
            type: "evidence-preview-error-handled",
          } as AssistantMessage;
        }
        return msg;
      })
    );

    const loadingMsg: AssistantMessage = {
      id: generateMessageId("assistant"),
      role: "assistant",
      type: "evidence-preview-loading",
    };
    setLoadingMessageId(loadingMsg.id);
    setMessages((prevMessages) => [...prevMessages, loadingMsg]);

    // Set the message to fetch, which will trigger the useQuery hook
    setMessageToFetch(userMessage.content);
  };

  return (
    <div className="flex flex-col gap-4 px-4">
      {messages.map((message) => (
        <AIAssistMessage
          key={message.id}
          message={message}
          handleUserMessage={handleUserMessage}
          handleRePrompt={handleRePrompt}
          handleGenerateEvidencePreview={handleGenerateEvidencePreview}
          handleSaveEvidencePreview={handleSaveEvidencePreview}
          handleRefineEvidencePreview={handleRefineEvidencePreview}
          handleUserRefineMessage={handleUserRefineMessage}
          handleRetryEvidencePreview={handleRetryEvidencePreview}
        />
      ))}
    </div>
  );
};

export default AIAssistFlow;
